const SIZE_GUIDE_STORAGE_PREFIX = 'intus-size-guide-';
const STORAGE_KEYS = {
  measurements: `${SIZE_GUIDE_STORAGE_PREFIX}measurements`,
  unit: `${SIZE_GUIDE_STORAGE_PREFIX}unit`,
  results: `${SIZE_GUIDE_STORAGE_PREFIX}results`,
  tab: `${SIZE_GUIDE_STORAGE_PREFIX}tab`,
};

const VALIDATION_RANGES = {
  bust: [60, 150],
  chest: [60, 150],
  waist: [50, 130],
  hips: [50, 150],
  height: [140, 210],
  weight: [30, 200],
  underbust: [55, 130],
  shoulder: [30, 60],
  inseam: [50, 100],
};

const GARMENT_WEIGHTS = {
  tops: { chest: 0.8, bust: 0.8, shoulder: 0.4, waist: 0.2 },
  bottoms: { waist: 0.8, hips: 0.8, hip: 0.8, inseam: 0.3 },
  'sports bras': { bust: 0.8, 'under bust': 0.8, underbust: 0.8 },
};

const ESTIMATION_COEFFICIENTS = {
  women: {
    bust:     { height: 0.15, weight: 0.45, intercept: 34 },
    waist:    { height: 0.10, weight: 0.57, intercept: 16 },
    hips:     { height: 0.12, weight: 0.43, intercept: 48 },
    shoulder: { height: 0.15, weight: 0.08, intercept: 9 },
    inseam:   { height: 0.47, weight: 0.00, intercept: -3 },
  },
  men: {
    chest:    { height: 0.20, weight: 0.52, intercept: 24 },
    waist:    { height: 0.08, weight: 0.70, intercept: 15 },
    inseam:   { height: 0.47, weight: 0.00, intercept: -1 },
  },
};

const SKIP_COLUMNS = new Set(['size', 'au', 'us', 'au/us', 'au / us', 'uk', 'eu', 'letter']);

const BETWEEN_SIZE_THRESHOLD = 0.15;

function debounce(fn, delay) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

function storageGet(key) {
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch {
    return null;
  }
}

function storageSet(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch { /* quota exceeded */ }
}

function storageRemove(key) {
  try {
    localStorage.removeItem(key);
  } catch { /* noop */ }
}

function trackEvent(name, payload = {}) {
  document.dispatchEvent(new CustomEvent(name, { detail: payload }));
  if (window.dataLayer) {
    window.dataLayer.push({ event: name, ...payload });
  }
}

function parseAudienceFromHandle(handle) {
  const h = handle.toLowerCase();
  if (h.includes('women') || h.includes('womens')) return 'women';
  if (h.includes('mens') || h.startsWith('men-') || h.includes('-men-')) return 'men';
  if (h.includes('unisex')) return 'unisex';
  return null;
}

function parseCategoryFromHandle(handle) {
  const lower = handle.toLowerCase();
  if (/bra|sports?\s*bra/i.test(lower)) return 'sports bras';
  if (/short|jogger|pant|legging|bottom|inseam/i.test(lower)) return 'bottoms';
  return 'tops';
}

function normalizeColumnName(col) {
  return col
    .trim()
    .toLowerCase()
    .replace(/\s*\(.*?\)\s*/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function findSizeColumn(columns) {
  return columns.findIndex(c => {
    const n = normalizeColumnName(c);
    return n === 'size' || n === 'au' || n === 'us' || n === 'au/us' || n === 'au / us';
  });
}

window.getOkendoFitRated = function () {
  const bars = document.querySelectorAll('.oke-attrBar-title');
  for (const bar of bars) {
    const text = bar.textContent || '';
    if (!/fitrated/i.test(text)) continue;
    const match = text.match(/FitRated\s+([-\d.]+)/i);
    if (match) {
      const value = parseFloat(match[1]);
      if (!Number.isNaN(value)) return value;
    }
  }
  return null;
};

window.getOkendoFitLabel = function (value) {
  if (value == null) return null;
  if (value < -0.3) return 'Runs small';
  if (value > 0.3) return 'Runs large';
  return 'True to size';
};

class SizeGuideHub extends HTMLElement {
  constructor() {
    super();
    this.cards = [];
    this.searchQuery = '';
    this.currentUnit = storageGet(STORAGE_KEYS.unit) || 'cm';
    this.heightUnit = 'cm';
    this.weightUnit = 'kg';
    this.currentTab = storageGet(STORAGE_KEYS.tab) || 'quick';
    this.measurements = storageGet(STORAGE_KEYS.measurements) || {};
    this.results = storageGet(STORAGE_KEYS.results) || null;
    this.isCollapsed = false;
  }

  connectedCallback() {
    this.cacheElements();
    this.bindEvents();
    this.restoreState();
    this.handleDeepLink();
    trackEvent('size_guide_view');
  }

  cacheElements() {
    this.cards = [...this.querySelectorAll('.size-guide-hub__card')];
    this.searchInput = this.querySelector('.size-guide-hub__search-input');
    this.noResults = this.querySelector('.size-guide-hub__no-results');
    this.findFitDetails = this.querySelector('[data-find-fit-details]');
    this.findFitForm = this.querySelector('.size-guide-hub__form');
    this.findFitSummary = this.querySelector('.size-guide-hub__find-fit-summary');
    this.summaryTags = this.querySelector('.size-guide-hub__summary-tags');
    this.summaryChevron = this.querySelector('.size-guide-hub__summary-chevron');
    this.submitButton = this.querySelector('[data-action="find-my-size"]');
    this.errorContainer = this.querySelector('[role="alert"]');
    this.tabButtons = [...this.querySelectorAll('.size-guide-hub__tab')];
    this.tabPanels = [...this.querySelectorAll('.size-guide-hub__tab-panel')];
    this.fieldToggles = [...this.querySelectorAll('.size-guide-hub__field-toggle')];
  }

  bindEvents() {
    if (this.searchInput) {
      this.searchInput.addEventListener('keyup', debounce(() => {
        this.searchQuery = this.searchInput.value.trim().toLowerCase();
        this.applyFilters();
      }, 150));
    }

    this.tabButtons.forEach(btn => {
      btn.addEventListener('click', () => this.switchTab(btn.dataset.tab));
    });

    this.fieldToggles.forEach(toggle => {
      toggle.addEventListener('click', () => {
        const btns = toggle.querySelectorAll('.size-guide-hub__unit-btn');
        const active = toggle.querySelector('.size-guide-hub__unit-btn--active');
        const other = [...btns].find(b => b !== active);
        if (other) this.handleFieldToggle(toggle, other);
      });
    });

    this.submitButton?.addEventListener('click', (e) => {
      e.preventDefault();
      this.handleSubmit();
    });

    if (this.findFitForm) {
      this.findFitForm.querySelectorAll('select[name]').forEach(sel => {
        sel.addEventListener('change', () => this.autoSubmit());
      });
      this.findFitForm.querySelectorAll('input[type="number"]').forEach(input => {
        input.addEventListener('blur', () => this.autoSubmit());
      });
    }

    this.findFitDetails?.addEventListener('toggle', () => {
      if (this.findFitDetails?.open) {
        if (this.measurements && Object.keys(this.measurements).length > 0) {
          this.prefillForm(this.measurements);
        }
        trackEvent('size_guide_find_my_fit_start');
      } else {
        const values = this.collectFormValues();
        if (values && Object.keys(values).length > 0) {
          this.measurements = values;
          this.updateSummaryTags();
        }
      }
    });

    this.cards.forEach(card => {
      const cta = card.querySelector('.size-guide-hub__card-cta');
      cta?.addEventListener('click', (e) => {
        e.preventDefault();
        this.openChart(card);
      });
    });
  }

  restoreState() {
    this.switchTab(this.currentTab, false);
    this.syncFieldToggles();

    if (this.measurements && Object.keys(this.measurements).length > 0) {
      this.prefillForm(this.measurements);
      if (this.results) {
        this.showResults(this.results);
      } else {
        this.runRecommender();
      }
      this.collapsePanel();
    }
  }

  handleDeepLink() {
    const hash = window.location.hash;
    if (!hash.startsWith('#chart=')) return;
    const handle = hash.slice(7);
    const card = this.cards.find(c => c.dataset.chartHandle === handle);
    if (card) {
      setTimeout(() => card.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
    }
  }

  // --- Per-field unit toggles ---

  handleFieldToggle(toggle, activeBtn) {
    const btns = toggle.querySelectorAll('.size-guide-hub__unit-btn');
    btns.forEach(b => b.classList.toggle('size-guide-hub__unit-btn--active', b === activeBtn));

    const field = toggle.dataset.toggleFor;
    const unit = activeBtn.dataset.unit;

    if (field === 'height') {
      this.heightUnit = (unit === 'cm' || unit === 'inch') ? unit : (unit === 'in' ? 'inch' : 'cm');
    } else if (field === 'weight') {
      this.weightUnit = (unit === 'kg' || unit === 'lbs') ? unit : 'kg';
    }

    this.updatePlaceholders();
    this.autoSubmit();
  }

  syncFieldToggles() {
    this.fieldToggles.forEach(toggle => {
      const field = toggle.dataset.toggleFor;
      const btns = toggle.querySelectorAll('.size-guide-hub__unit-btn');
      btns.forEach(btn => {
        const u = btn.dataset.unit;
        let active = false;
        if (field === 'height') {
          active = (this.heightUnit === 'cm' && u === 'cm') || (this.heightUnit === 'inch' && (u === 'inch' || u === 'in'));
        } else if (field === 'weight') {
          active = (this.weightUnit === 'kg' && u === 'kg') || (this.weightUnit === 'lbs' && u === 'lbs');
        }
        btn.classList.toggle('size-guide-hub__unit-btn--active', active);
      });
    });
    this.updatePlaceholders();
  }

  updatePlaceholders() {
    if (!this.findFitForm) return;
    const heightPh = this.heightUnit === 'inch' ? 'Height (in)' : 'Height (cm)';
    const weightPh = this.weightUnit === 'lbs' ? 'Weight (lbs)' : 'Weight (kg)';
    this.findFitForm.querySelectorAll('[name="height"]').forEach(el => { el.placeholder = heightPh; });
    this.findFitForm.querySelectorAll('[name="weight"]').forEach(el => { el.placeholder = weightPh; });
  }

  // --- Filtering (search only — audience filtering disabled while Find My Fit is hidden) ---

  applyFilters() {
    let visibleCount = 0;

    this.cards.forEach(card => {
      const title = (card.dataset.title || '').toLowerCase();
      const handle = (card.dataset.chartHandle || '').toLowerCase();
      const usedBy = (card.dataset.usedBy || '').toLowerCase();

      const matchesSearch = !this.searchQuery ||
        title.includes(this.searchQuery) ||
        handle.includes(this.searchQuery) ||
        usedBy.includes(this.searchQuery);

      card.toggleAttribute('hidden', !matchesSearch);
      if (matchesSearch) visibleCount++;
    });

    if (this.noResults) {
      this.noResults.toggleAttribute('hidden', visibleCount > 0);
    }
  }

  // --- Tabs ---

  switchTab(tab, persist = true) {
    this.currentTab = tab;
    this.tabButtons.forEach(btn => {
      btn.classList.toggle('size-guide-hub__tab--active', btn.dataset.tab === tab);
    });
    this.tabPanels.forEach(panel => {
      panel.toggleAttribute('hidden', panel.dataset.tab !== tab);
    });
    if (persist) storageSet(STORAGE_KEYS.tab, tab);
  }

  // --- Form handling ---

  collectFormValues() {
    const form = this.findFitForm;
    if (!form) return {};

    const activePanel = form.querySelector(`.size-guide-hub__tab-panel[data-tab="${this.currentTab}"]`);
    if (!activePanel) return {};

    const values = {};
    const inputs = activePanel.querySelectorAll('input[name], select[name]');
    inputs.forEach(input => {
      const val = input.value.trim();
      if (!val) return;
      let parsed = input.type === 'number' ? parseFloat(val) : val;
      if (input.name === 'height' && this.heightUnit === 'inch' && typeof parsed === 'number') {
        parsed = Math.round(parsed * 2.54);
      }
      if (input.name === 'weight' && this.weightUnit === 'lbs' && typeof parsed === 'number') {
        parsed = Math.round(parsed / 2.205);
      }
      values[input.name] = parsed;
    });
    return values;
  }

  prefillForm(data) {
    if (!this.findFitForm || !data) return;
    Object.entries(data).forEach(([key, value]) => {
      const inputs = this.findFitForm.querySelectorAll(`[name="${key}"]`);
      inputs.forEach(input => { input.value = value; });
    });
  }

  validateInputs(values) {
    const errors = [];

    if (!values.audience) {
      errors.push('Please select an audience.');
      return errors;
    }

    if (this.currentTab === 'quick') {
      if (!values.height) errors.push('Height is required.');
      if (!values.weight) errors.push('Weight is required.');
      if (values.height && !this.inRange('height', values.height)) {
        errors.push(`Height must be between ${VALIDATION_RANGES.height[0]} and ${VALIDATION_RANGES.height[1]} cm.`);
      }
      if (values.weight && !this.inRange('weight', values.weight)) {
        errors.push(`Weight must be between ${VALIDATION_RANGES.weight[0]} and ${VALIDATION_RANGES.weight[1]} kg.`);
      }
    } else {
      const hasAnyMeasurement = ['bust', 'chest', 'waist', 'hips', 'underbust', 'shoulder', 'inseam']
        .some(f => values[f]);
      if (!hasAnyMeasurement) {
        errors.push('Please enter at least one measurement.');
      }

      Object.entries(values).forEach(([field, value]) => {
        if (typeof value === 'number' && !this.inRange(field, value)) {
          const [min, max] = VALIDATION_RANGES[field] || [0, 999];
          errors.push(`${field.charAt(0).toUpperCase() + field.slice(1)} must be between ${min} and ${max} cm.`);
        }
      });
    }

    return errors;
  }

  inRange(field, value) {
    const range = VALIDATION_RANGES[field];
    if (!range) return true;
    return value >= range[0] && value <= range[1];
  }

  showError(message) {
    if (!this.errorContainer) return;
    this.errorContainer.textContent = message;
    this.errorContainer.removeAttribute('hidden');
  }

  clearError() {
    if (!this.errorContainer) return;
    this.errorContainer.textContent = '';
    this.errorContainer.setAttribute('hidden', '');
  }

  autoSubmit() {
    this.clearError();
    const values = this.collectFormValues();
    const errors = this.validateInputs(values);
    if (errors.length) return;

    this.measurements = values;
    storageSet(STORAGE_KEYS.measurements, values);
    this.runRecommender();
  }

  handleSubmit() {
    this.clearError();
    const values = this.collectFormValues();
    const errors = this.validateInputs(values);

    if (errors.length) {
      this.showError(errors[0]);
      return;
    }

    this.measurements = values;
    storageSet(STORAGE_KEYS.measurements, values);

    trackEvent('size_guide_find_my_fit_submit', {
      audience: values.audience,
      input_mode: this.currentTab,
      unit: this.currentUnit,
    });

    this.runRecommender();
    this.collapsePanel();
  }

  // --- Recommender ---

  runRecommender() {
    const values = this.measurements;
    if (!values) return;

    const audience = values.audience || 'women';
    const isQuick = this.currentTab === 'quick' || (values.height && values.weight && !values.bust && !values.chest);
    let bodyMeasurements;

    if (isQuick) {
      const primaryEst = this.estimateMeasurements(audience, values.height, values.weight);
      const secondaryEst = this.estimateMeasurements(audience === 'men' ? 'women' : 'men', values.height, values.weight);
      bodyMeasurements = {
        height: values.height,
        weight: values.weight,
        ...primaryEst,
        ...(audience === 'women' ? { chest: secondaryEst.chest } : { bust: secondaryEst.bust, hips: secondaryEst.hips }),
      };
      bodyMeasurements._estimated = true;
    } else {
      bodyMeasurements = { ...values };
      bodyMeasurements._estimated = false;
    }

    const results = {};

    this.cards.forEach(card => {
      const handle = card.dataset.chartHandle;
      const chartJson = card.dataset.chartJson;
      if (!handle || !chartJson) return;

      let tableData;
      try {
        tableData = typeof chartJson === 'string' ? JSON.parse(chartJson) : chartJson;
      } catch (e) {
        console.warn(`[SizeGuide] Failed to parse chart JSON for ${handle}:`, e.message);
        return;
      }

      if (!tableData || typeof tableData !== 'object') {
        console.warn(`[SizeGuide] Invalid chart data for ${handle}:`, typeof tableData);
        return;
      }

      const unitData = tableData.cm || tableData;
      const category = parseCategoryFromHandle(handle);
      const recommendation = this.computeRecommendation(unitData, bodyMeasurements, category);

      if (recommendation) {
        results[handle] = {
          match: true,
          size: recommendation.size,
          betweenSizes: recommendation.betweenSizes,
          nextSize: recommendation.nextSize,
          confidence: bodyMeasurements._estimated ? 'estimated' : 'measured',
          basedOn: recommendation.basedOn,
        };
      }
    });

    this.results = results;
    storageSet(STORAGE_KEYS.results, results);
    this.showResults(results);

    trackEvent('size_guide_result_view', {
      results: Object.entries(results)
        .filter(([, r]) => r.match)
        .map(([handle, r]) => ({ handle, size: r.size })),
    });
  }

  estimateMeasurements(audience, height, weight) {
    const coeffs = audience === 'men'
      ? ESTIMATION_COEFFICIENTS.men
      : ESTIMATION_COEFFICIENTS.women;

    const estimated = {};
    Object.entries(coeffs).forEach(([measurement, c]) => {
      estimated[measurement] = Math.round(
        c.height * height + c.weight * weight + c.intercept
      );
    });

    return estimated;
  }

  computeRecommendation(tableData, measurements, category) {
    const columns = tableData.columns;
    const rows = tableData.rows;
    if (!columns?.length || !rows?.length) return null;

    const sizeColIndex = findSizeColumn(columns);
    if (sizeColIndex === -1) return null;

    const weights = GARMENT_WEIGHTS[category] || GARMENT_WEIGHTS.tops;

    const colMap = columns.map((col, i) => {
      const normalized = normalizeColumnName(col);
      if (SKIP_COLUMNS.has(normalized)) return null;

      const weight = weights[normalized]
        || weights[normalized.replace(/\s/g, '')]
        || weights[normalized.replace('under ', 'under')];
      if (!weight) return null;

      const lookupKeys = [
        normalized,
        normalized.replace(/\s/g, ''),
        normalized.replace('under ', 'under'),
        normalized === 'hip' ? 'hips' : null,
        normalized === 'hips' ? 'hip' : null,
      ].filter(Boolean);
      const userValue = lookupKeys.reduce((v, k) => v || measurements[k], null);
      if (!userValue) return null;

      const midRowIdx = Math.floor(rows.length / 2);
      const medianCell = this.parseCellValue(rows[midRowIdx]?.[i]);
      let scale = 1;
      if (medianCell && userValue) {
        const ratio = userValue / medianCell;
        if (ratio > 3) return null;
        if (ratio > 1.6) scale = 2;
        else if (ratio < 0.35) return null;
      }

      return { index: i, weight, userValue, name: normalized, scale };
    }).filter(Boolean);

    if (colMap.length === 0) return null;

    const scored = rows.map((row, rowIndex) => {
      let totalWeightedDist = 0;
      let totalWeight = 0;

      colMap.forEach(({ index, weight, userValue, scale }) => {
        const rawCell = this.parseCellValue(row[index]);
        if (rawCell == null) return;
        const cellValue = rawCell * scale;
        const dist = Math.abs(userValue - cellValue);
        totalWeightedDist += dist * weight;
        totalWeight += weight;
      });

      const score = totalWeight > 0 ? totalWeightedDist / totalWeight : Infinity;
      return { rowIndex, score, size: row[sizeColIndex] };
    });

    scored.sort((a, b) => a.score - b.score);

    const best = scored[0];
    if (!best || best.score === Infinity) return null;

    const primaryMeasurement = colMap.reduce((a, b) => a.weight > b.weight ? a : b);

    const result = {
      size: best.size,
      betweenSizes: false,
      nextSize: null,
      basedOn: primaryMeasurement.name,
    };

    if (scored.length > 1) {
      const second = scored[1];
      const diff = Math.abs(best.score - second.score);
      const threshold = best.score * BETWEEN_SIZE_THRESHOLD;
      if (diff <= threshold && best.size !== second.size) {
        result.betweenSizes = true;
        if (best.rowIndex < second.rowIndex) {
          result.size = best.size;
          result.nextSize = second.size;
        } else {
          result.size = second.size;
          result.nextSize = best.size;
        }
      }
    }

    return result;
  }

  parseCellValue(cell) {
    if (cell == null || cell === '') return null;
    const str = String(cell).trim();
    const rangeMatch = str.match(/^([\d.]+)\s*[-–]\s*([\d.]+)$/) || str.match(/^([\d.]+)\s{2,}([\d.]+)$/);
    if (rangeMatch) {
      return (parseFloat(rangeMatch[1]) + parseFloat(rangeMatch[2])) / 2;
    }
    const ltGtMatch = str.match(/^[<>]\s*([\d.]+)$/);
    if (ltGtMatch) {
      return parseFloat(ltGtMatch[1]);
    }
    const num = parseFloat(str);
    return Number.isNaN(num) ? null : num;
  }

  // --- Results display ---

  showResults(results) {
    this.cards.forEach(card => {
      const handle = card.dataset.chartHandle;
      const recEl = card.querySelector('.size-guide-hub__card-recommendation');
      const recSize = card.querySelector('.size-guide-hub__rec-size');
      const recConfidence = card.querySelector('.size-guide-hub__rec-confidence');
      const result = results?.[handle];

      card.classList.remove('size-guide-hub__card--faded');
      if (recEl) recEl.setAttribute('hidden', '');
      if (recSize) recSize.textContent = '';
      if (recConfidence) recConfidence.textContent = '';

      if (!result) return;

      if (!result.match) {
        card.classList.add('size-guide-hub__card--faded');
        return;
      }

      if (recEl) {
        recEl.removeAttribute('hidden');

        let sizeText = result.size;
        if (result.betweenSizes && result.nextSize) {
          sizeText = `${result.size}–${result.nextSize}`;
        }

        if (recSize) recSize.textContent = sizeText;

        if (recConfidence) {
          const basedOn = result.basedOn
            ? result.basedOn.charAt(0).toUpperCase() + result.basedOn.slice(1)
            : null;

          if (result.confidence === 'estimated') {
            recConfidence.textContent = 'Based on height/weight estimate';
          } else if (basedOn) {
            recConfidence.textContent = `Based on ${basedOn.toLowerCase()}`;
          } else {
            recConfidence.textContent = 'Based on your measurements';
          }
        }
      }
    });

    this.applyFilters();
  }

  clearResults() {
    this.cards.forEach(card => {
      card.classList.remove('size-guide-hub__card--faded');
      const recEl = card.querySelector('.size-guide-hub__card-recommendation');
      if (recEl) recEl.setAttribute('hidden', '');
    });
  }

  // --- Panel collapse/expand ---

  collapsePanel() {
    this.isCollapsed = true;
    if (this.findFitDetails) {
      this.findFitDetails.removeAttribute('open');
      const values = this.collectFormValues();
      if (values && Object.keys(values).length > 0) {
        this.measurements = values;
      }
      this.updateSummaryTags();
    }
  }

  expandPanel() {
    this.isCollapsed = false;
    if (this.findFitDetails) {
      this.findFitDetails.setAttribute('open', '');
      if (this.measurements && Object.keys(this.measurements).length > 0) {
        this.prefillForm(this.measurements);
      }
    }
  }

  updateSummaryTags() {
    if (!this.summaryTags || !this.measurements) return;
    this.summaryTags.innerHTML = '';

    const entries = [];
    const m = this.measurements;

    if (m.audience) entries.push({ label: '', value: m.audience.charAt(0).toUpperCase() + m.audience.slice(1) });
    if (m.height) entries.push({ label: 'Height', value: `${m.height}cm` });
    if (m.weight) entries.push({ label: 'Weight', value: `${m.weight}kg` });
    if (m.bust) entries.push({ label: 'Bust', value: `${m.bust}cm` });
    if (m.chest) entries.push({ label: 'Chest', value: `${m.chest}cm` });
    if (m.waist) entries.push({ label: 'Waist', value: `${m.waist}cm` });
    if (m.hips) entries.push({ label: 'Hips', value: `${m.hips}cm` });

    entries.forEach(({ label, value }) => {
      const tag = document.createElement('span');
      tag.className = 'size-guide-hub__summary-tag';
      if (label) {
        tag.innerHTML = `<span class="size-guide-hub__summary-tag-label">${label}</span> <span class="size-guide-hub__summary-tag-value">${value}</span>`;
      } else {
        tag.innerHTML = `<span class="size-guide-hub__summary-tag-value">${value}</span>`;
      }
      this.summaryTags.appendChild(tag);
    });
  }

  // --- Edit / Clear ---

  clearAll() {
    this.measurements = {};
    this.results = null;
    this.isCollapsed = false;

    Object.values(STORAGE_KEYS).forEach(key => storageRemove(key));

    this.clearResults();
    this.applyFilters();
    this.expandPanel();

    if (this.findFitForm) {
      this.findFitForm.querySelectorAll('input, select').forEach(el => {
        if (el.type === 'number' || el.type === 'text') el.value = '';
        if (el.tagName === 'SELECT') el.selectedIndex = 0;
      });
    }

    this.clearError();
    this.switchTab('quick');
    this.currentUnit = 'cm';
    this.heightUnit = 'cm';
    this.weightUnit = 'kg';
    storageSet(STORAGE_KEYS.unit, 'cm');
    this.syncFieldToggles();
  }

  // --- Chart drawer ---

  openChart(card) {
    const handle = card.dataset.chartHandle;
    const template = card.querySelector('template');

    trackEvent('size_guide_chart_open', { chart_handle: handle });
    trackEvent('size_guide_entry_point', { source: 'hub' });

    if (template && typeof window.openSizeChartDrawer === 'function') {
      const content = template.content.cloneNode(true);
      const wrapper = document.createElement('div');
      wrapper.appendChild(content);

      const result = this.results?.[handle];
      if (result?.match && result.size) {
        wrapper.dataset.recommendedSize = result.size;
      }

      window.openSizeChartDrawer(wrapper.innerHTML);
    }
  }
}

customElements.define('size-guide-hub', SizeGuideHub);
